import { useEffect, useState, useRef, useContext } from "react";
import { Context } from "../../../Context";
import { SunlightPatchesRenderer } from "./SunlightPatchesRenderer";
import { getBoundaryPoints } from "../utils/geometryUtils"; 
import styles from "../../../styles/aprt.module.scss";
import { insolationFillColors } from "./InsolationController";

function PlanRendererInsolationMode({
  svgContent,
  svgDimensions,
  rotationAngleDeg,
  insolationAngle,
  // apartmentData,
  selectedCompartmentInd,
  hoveredCompartmentInd,
  setApartmentViewInteractions,
  compartmentsDictionary
}) {
  const { activeApartmentData } = useContext(Context);

  const [modifiedSvgContent, setModifiedSvgContent] = useState(svgContent);
  const [windowsOpeningsData, setWindowsOpeningsData] = useState([]);
  const svgContainerRef = useRef(null);
  const insolatedCompartmentsNumbers = activeApartmentData.compartments
    .filter((compartment) => compartment.window_area > 0)
    .map((compartment) => compartment.number);
    
    //TODO: move to utils
    const sumSunLightRanges = (arr) => {
      if (!arr || arr.length === 0) return 0;
      let sum = 0;
      arr.forEach((range) => {
        sum += range[1] - range[0];
      });
      return sum;
    };

    const getOutdoorCompartmentNameIds = () =>
      // compartmentsDictionary?.compartments
      compartmentsDictionary
        .filter((room) => room.group === "OutdoorSpace")
        .map((room) => room.id);
  const outdoorCompartmentIds = getOutdoorCompartmentNameIds();

  const handleRoomHover = (ind, isOnPath) => {
    //TODO: move this to utils
    ind === null
      ? setApartmentViewInteractions(
          ({ isCompartmentPathHovered, hoveredCompartmentInd, ...rest }) => ({
            isCompartmentPathHovered: false,
            hoveredCompartmentInd: null,
            ...rest,
          })
        )
      : setApartmentViewInteractions(
          ({ isCompartmentPathHovered, hoveredCompartmentInd, ...rest }) => ({
            isCompartmentPathHovered: isOnPath,
            hoveredCompartmentInd: ind,
            ...rest,
          })
        );
  };
  const handleRoomClick = (ind) => {
    // selectedCompartmentInd === ind //TODO: something is not working here
    !ind
      ? setApartmentViewInteractions(({ selectedCompartmentInd, ...rest }) => ({
          selectedCompartmentInd: null,
          ...rest,
        }))
      : setApartmentViewInteractions(({ selectedCompartmentInd, ...rest }) => ({
          selectedCompartmentInd: ind,
          ...rest,
        }));
  };

  useEffect(() => {
    if (!svgContent) return;
    if (!activeApartmentData) return;

    const getWindowsOpeningsData = (svgContent) => {
      let openingsData = [];
      const windowsGroup = svgContent.querySelectorAll(".openings_window");

      for (const group of windowsGroup) {
        const windows = group.querySelectorAll("g");

        for (const window of windows) {
          const windowInd = window.getAttribute("id_hex");
          const windowTransform = window.getAttribute("transform");
          const sillPath = window.querySelector(".opening_sill");
          const sillPathData = sillPath.getAttribute("d");

          const getInsertPointAndAngleFromTransform = (transform) => {
            const translateMatch = transform.match(
              /translate\(([-+]?[0-9]*\.?[0-9]+),\s*([-+]?[0-9]*\.?[0-9]+)\)/
            );
            const rotateMatch = transform.match(
              /rotate\(([-+]?[0-9]*\.?[0-9]+)\)/
            );

            let insertX = 0,
              insertY = 0,
              angle = 0;

            if (translateMatch) {
              insertX = parseFloat(translateMatch[1]);
              insertY = parseFloat(translateMatch[2]);
            }

            if (rotateMatch) {
              angle = parseFloat(rotateMatch[1]);
            }

            return { angle, insertX, insertY };
          };

          const openingData = {
            id_hex: windowInd,
            boundary: getBoundaryPoints(sillPathData),
            angle: getInsertPointAndAngleFromTransform(windowTransform).angle,
            insert_point: [
              getInsertPointAndAngleFromTransform(windowTransform).insertX,
              getInsertPointAndAngleFromTransform(windowTransform).insertY,
            ],
          };

          openingsData.push(openingData);
        }
      }

      openingsData = openingsData.map((opening) => {
        const apartmentWindowsData = activeApartmentData.tiers[0].windows.find(
          //TODO: important!! this will fail if there are more than one tiers in the apartment
          (window) => window.id_hex === opening.id_hex
        );
        return {
          ...opening,
          sun_light_ranges: apartmentWindowsData?.sun_light_ranges ?? [],
          insolation: sumSunLightRanges(apartmentWindowsData?.sun_light_ranges ?? []),
        };
      });

      return openingsData;
    };

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
    const svgRoot = svgDoc.documentElement;

    svgRoot.setAttribute("transform", "scale(0.85)");
    // Apply compartment fill colors based on insolation
    //TODO: fix: This code executes unnecessary on insolationAngle change
    const compartmentsGroup = svgRoot.querySelector('.compartments');
    const compartmentPaths =
      compartmentsGroup.querySelectorAll('.compartment');
      for (const path of compartmentPaths) {
        const pathInd = path.getAttribute("number");

      if (pathInd) {
        const compartmentData = activeApartmentData.compartments.find(
          (compartment) => compartment.number === +pathInd
        );
        if (compartmentData) {
          const compartmentInsolation = sumSunLightRanges(compartmentData.sun_light_ranges);
          const insolationValue =
            compartmentInsolation != undefined
              ? Math.floor(compartmentInsolation)
              : 0;

          const fillColor = insolatedCompartmentsNumbers.includes(
            compartmentData.number
          )
            ? insolationFillColors[insolationValue]
            : outdoorCompartmentIds.includes(compartmentData.name) //TODO: use compartment_type.json or new compartments.json to find outdoor compartments
            ? "#ffffff"
            : "#dddddd";

          path.setAttribute("style", `fill:${fillColor}`);
        }
      }
    }

    // Generate and append sunlight patches
    const openingsData = getWindowsOpeningsData(svgRoot);
    if (openingsData) {
      const generatedSvgContent = SunlightPatchesRenderer({
        openingsData,
        svgDimensions,
        rotationAngleDeg,
        insolationAngle,
      });

      const generatedSvgDoc = parser.parseFromString(
        generatedSvgContent,
        "image/svg+xml"
      );
      const generatedSvgElement = generatedSvgDoc.documentElement;

      svgRoot.appendChild(generatedSvgElement);
    }

    // Serialize and set the modified SVG content
    const serializer = new XMLSerializer();
    const newSvgContent = serializer.serializeToString(svgDoc);
    setModifiedSvgContent(newSvgContent);
  }, [activeApartmentData, svgContent, insolationAngle]);

  useEffect(() => {
    const svgContainer = svgContainerRef.current;
    if (!svgContainer) return;

    const compartmentPaths = svgContainer.querySelectorAll(
      ".compartments > g > path.compartment"
    );

    compartmentPaths.forEach((path) => {
      const compartmentNumber = Number(path.getAttribute("number"));
      //TODO: test listeners on mobile devices, maybe use onTouchStart instead of onClick
      path.addEventListener("click", () => handleRoomClick(compartmentNumber));
      path.addEventListener("mouseenter", () =>
        handleRoomHover(compartmentNumber, true)
      );
      path.addEventListener("mouseleave", () => handleRoomHover(null, false));
    });

    return () => {
      compartmentPaths.forEach((path) => {
        const compartmentNumber = Number(path.getAttribute("number"));
        path.removeEventListener("click", () => handleRoomClick(compartmentNumber));
        path.removeEventListener(
          "mouseenter",
          handleRoomHover(compartmentNumber, true)
        );
        path.removeEventListener("mouseleave", handleRoomHover(null, false));
      });
    };
  }, [modifiedSvgContent]);

  useEffect(() => {
    //TODO: check if not merge with above useEffect[svgContent]
    const svgContainer = svgContainerRef.current;
    if (!svgContainer) return;

    const compartmentPaths = svgContainer.querySelectorAll(
      ".compartments > g > path.compartment"
    );

    compartmentPaths.forEach((path) => {
      const compartmentNumber = Number(path.getAttribute("number"));
      //TODO: test listeners on mobile devices, maybe use onTouchStart instead of onClick
      if (compartmentNumber === selectedCompartmentInd || compartmentNumber === hoveredCompartmentInd) {
        path.classList.add(`${styles.svg_path_highlighted}`);
      }
      if (compartmentNumber != selectedCompartmentInd && compartmentNumber != hoveredCompartmentInd) {
        path.classList.remove(`${styles.svg_path_highlighted}`);
      }
    });

  }, [selectedCompartmentInd, hoveredCompartmentInd]);


  return (
    <div
      ref={svgContainerRef}
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontFamily: "Lato", //TODO: not working, anyway use Lato globally as main font
      }}
      dangerouslySetInnerHTML={{ __html: modifiedSvgContent }}
    />
  );
}

export default PlanRendererInsolationMode;